import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Stethoscope, Briefcase, HardHat, UtensilsCrossed, Home } from "lucide-react";
import { MaskedLines, Reveal, ChapterNumber } from "../components/motion";

const industries = [
  {
    icon: Building2,
    title: "Energy and oilfield services.",
    body: "Engineering firms, field-service companies and suppliers often have a mix of office staff and crews who travel. The plan has to make sense for both, and for families spread across the region.",
  },
  {
    icon: Stethoscope,
    title: "Healthcare practices.",
    body: "Medical, dental and therapy offices know insurance from the patient side. As employers, they often want a plan that is simple to explain to staff and easy to administer.",
  },
  {
    icon: Briefcase,
    title: "Professional services.",
    body: "CPA firms, law offices, engineering and architecture practices compete for talent with large employers nearby. Benefits are part of how they recruit and keep good people.",
  },
  {
    icon: HardHat,
    title: "Construction and the trades.",
    body: "Builders, contractors and specialty trades often have workers who weigh take-home pay against coverage. Clear contribution choices matter here.",
  },
  {
    icon: UtensilsCrossed,
    title: "Retail, restaurants and hospitality.",
    body: "Hourly and part-time teams need eligibility rules and waiting periods set up carefully, so the right people are offered coverage at the right time.",
  },
  {
    icon: Home,
    title: "Technology, real estate and family-owned companies.",
    body: "Smaller teams that want a real person to call, not a portal and a ticket number.",
  },
];

const renewalSteps = [
  {
    title: "October to November: the renewal arrives.",
    body: "Your carrier sends the renewal for next year, usually 60 to 90 days before the effective date. It shows next year's rates and any changes to plan designs. It is easy to set it aside during a busy fourth quarter. Please don't.",
  },
  {
    title: "November: review and compare.",
    body: "I read the renewal line by line with you, flag what changed, and look at the options available to a group your size. That can include adjusting plan designs, adding a second plan option, changing how you split the cost with employees, or looking at a different funding approach. Every option comes with its trade-offs written out plainly.",
  },
  {
    title: "Late November to mid-December: decide and enroll.",
    body: "Once you choose, employees need time to make their elections. I can help with a short enrollment meeting, a one-page summary in plain language, and answers to the questions people are shy to ask in front of coworkers.",
  },
  {
    title: "January: the plan takes effect.",
    body: "ID cards arrive, payroll deductions start, and new questions come up. I'm still here for those.",
  },
];

const brokerQuestions = [
  {
    title: "Does my plan change?",
    body: "No. Your plan, your employees' coverage and their ID cards stay as they are. Only the person servicing the account changes.",
  },
  {
    title: "Do I have to wait for renewal?",
    body: "No. A broker of record change can happen at any time of year. Many employers make the change in the fall so the new broker can work on the January 1 renewal.",
  },
  {
    title: "Do I need to have an awkward conversation?",
    body: "The carrier notifies the prior broker. You are welcome to tell them yourself, but it isn't required.",
  },
];

const faq = [
  {
    q: "Do you only work with companies in The Woodlands?",
    a: "No. I serve The Woodlands, Houston and surrounding communities, and I can meet anywhere in the area or by video.",
  },
  {
    q: "How many employees do we need for a group health plan?",
    a: "In Texas, small group coverage generally starts at two employees, and carriers set their own participation and contribution rules. Most of the employers I work with have between 10 and 150 employees, but I'm happy to talk with smaller teams too.",
  },
  {
    q: "When should we start on a January 1 renewal?",
    a: "As soon as the renewal arrives, ideally in October or early November. That leaves time to compare options, make a decision and give employees a fair enrollment period before the deadline.",
  },
  {
    q: "Will it cost us anything to work with you?",
    a: "There's no fee to you; carriers pay my commission when a plan is placed or renewed with me as your broker.",
  },
  {
    q: "Can we switch brokers in the middle of the year?",
    a: "Yes. A broker of record letter can be signed at any time, and your plan stays in place.",
  },
  {
    q: "What should I have ready for our first call?",
    a: "Your current renewal or plan summary if you have one, a rough headcount, and a list of what's working and what isn't. A full employee census can come later.",
  },
];

export default function GroupBenefitsWoodlands() {
  return (
    <div data-testid="page-group-benefits-the-woodlands">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-36 lg:px-10 lg:pt-44" data-testid="woodlands-hero">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="woodlands-kicker">
          Group Health · Employee Benefits · The Woodlands, TX
        </p>
        <MaskedLines
          as="h1"
          className="mt-8 max-w-4xl font-serif text-4xl font-light leading-[1.02] tracking-tighter text-forest sm:text-5xl lg:text-6xl"
          lines={["Group Health Insurance Broker in The Woodlands"]}
        />
        <Reveal delay={0.35}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
            Good benefits for the people who make your business work. I&rsquo;m Kyle Walker, a licensed Texas
            health insurance agent who was born and raised in The Woodlands, and I help local employers set up,
            renew and understand their group health plans. When you work with OneHarvestCo. Benefits, you work
            with me, from the first census to the questions your team asks in March.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="woodlands-hero-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
            >
              Talk with Kyle
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/employers"
              data-testid="woodlands-employers-link"
              className="group inline-flex items-center gap-2 rounded-full border border-forest/30 px-8 py-3.5 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:text-terra"
            >
              See how I work with employers
            </Link>
          </div>
        </Reveal>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-sand/70" data-testid="woodlands-industries">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <ChapterNumber n="01" />
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Built for the businesses that make up The Woodlands
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
              The Woodlands, Houston and surrounding communities are home to a wide mix of employers, and each
              one brings different benefit questions. Most of the companies I talk with have somewhere between
              10 and 150 employees.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((item, i) => (
              <Reveal key={item.title} delay={0.06 * i}>
                <div
                  className="h-full border border-border bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60"
                  data-testid={`woodlands-industry-${i}`}
                >
                  <item.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl font-medium tracking-tight text-forest">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-12 max-w-3xl text-base leading-relaxed text-ink/70">
              No matter the industry, the job is the same: understand your people, lay out the real options side
              by side, and help you choose what fits your budget and your team.
            </p>
          </Reveal>
        </div>
      </section>

      {/* RENEWAL */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="woodlands-renewal">
        <Reveal>
          <ChapterNumber n="02" />
          <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            What a January 1 renewal looks like
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
            Many employers around The Woodlands renew their group health plan on January 1. Here is how the
            season usually runs:
          </p>
        </Reveal>
        <div className="mt-12">
          {renewalSteps.map((step, i) => (
            <Reveal key={step.title} delay={0.05 * i}>
              <div
                className={`flex gap-6 border-t border-border py-7 ${i === renewalSteps.length - 1 ? "border-b" : ""}`}
                data-testid={`woodlands-renewal-step-${i}`}
              >
                <span className="font-mono text-xs tracking-[0.3em] text-terra">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif text-xl font-medium tracking-tight text-forest sm:text-2xl">
                    {step.title}
                  </h3>
                  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink/65">{step.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-10 max-w-3xl text-base leading-relaxed text-ink/70">
            One local detail worth knowing: for small groups, federal rules create an annual window from
            November 15 to December 15 when carriers can&rsquo;t hold a new group to their usual minimum
            participation or employer contribution rules for a January 1 start. If your company has struggled
            to meet those requirements, that window can matter. Ask me whether it applies to you.
          </p>
        </Reveal>
      </section>

      {/* BROKER OF RECORD */}
      <section className="bg-sand/70" data-testid="woodlands-broker-of-record">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <ChapterNumber n="03" />
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Changing brokers: what a broker of record letter does
            </h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-ink/70">
              If you already have a group plan and want a different broker, you don&rsquo;t have to change the
              plan to make the switch. You sign a short <strong className="font-medium text-forest">broker of
              record letter</strong> on company letterhead that names your new broker. The carrier processes it,
              usually within a few business days, and from then on your new broker can see your account, handle
              service issues and work on your renewal.
            </p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
              A few things people often ask:
            </p>
          </Reveal>
          <div className="mt-10">
            {brokerQuestions.map((item, i) => (
              <Reveal key={item.title} delay={0.05 * i}>
                <div
                  className={`border-t border-border py-7 ${i === brokerQuestions.length - 1 ? "border-b" : ""}`}
                  data-testid={`woodlands-bor-question-${i}`}
                >
                  <h3 className="font-serif text-xl font-medium tracking-tight text-forest sm:text-2xl">
                    {item.title}
                  </h3>
                  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink/65">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW I WORK */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="woodlands-how-i-work">
        <Reveal>
          <ChapterNumber n="04" />
          <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            How I work with employers
          </h2>
          <div className="mt-8 max-w-3xl space-y-6">
            <p className="text-base leading-relaxed text-ink/70">
              I meet clients the way that suits them: at your office, over coffee somewhere in town, by phone,
              or on a video call. Most first conversations take about 30 minutes. I&rsquo;ll ask about your
              team, your current plan if you have one, your renewal date and what isn&rsquo;t working. Then
              I&rsquo;ll tell you honestly what I&rsquo;d look at first.
            </p>
            <p className="text-base leading-relaxed text-ink/70">
              There&rsquo;s no fee to you for my help; carriers pay my commission. That&rsquo;s how group health
              brokerage works in Texas, and I&rsquo;m glad to explain it in plain terms.
            </p>
            <p className="text-base leading-relaxed text-ink/70">
              If your company uses a PEO, or you&rsquo;re wondering whether one makes sense, I help with that
              too. You can read more on the{" "}
              <Link
                to="/employers"
                data-testid="woodlands-employers-page-link"
                className="font-medium text-terra underline-offset-4 transition-colors duration-300 hover:text-forest hover:underline"
              >
                Employers page
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="bg-sand/70" data-testid="woodlands-faq">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <ChapterNumber n="05" />
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Questions employers in The Woodlands ask
            </h2>
          </Reveal>
          <div className="mt-12 max-w-3xl space-y-8">
            {faq.map((item, i) => (
              <Reveal key={item.q} delay={0.05 * i}>
                <div data-testid={`woodlands-faq-${i}`}>
                  <h3 className="font-serif text-xl font-medium tracking-tight text-forest sm:text-2xl">
                    {item.q}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-ink/70">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="woodlands-cta">
        <Reveal>
          <h2 className="max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            Let&rsquo;s talk about your team
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
            Whether your renewal just landed on your desk or you&rsquo;re thinking about offering coverage for
            the first time, the first step is a simple conversation. Call or text me at (713) 725-7413, email
            kyle@ohcbenefits.com, or send a note through the contact page.
          </p>
          <Link
            to="/contact"
            data-testid="woodlands-bottom-cta"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-forest px-9 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
          >
            Request a consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
